import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Mail, Phone, ExternalLink, Send } from "lucide-react";
import { WEB3FORMS_ACCESS_KEY } from "../config";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: "New Contact Form Submission - GSLC",
          from_name: "GSLC Website",
          ...formData
        }),
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success("Message sent successfully! We'll get back to you soon.");
        setFormData({ name: '', email: '', phone: '', organization: '', message: '' });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24">
      {/* Hero */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-red-50 via-white to-blue-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Get In Touch</span>
            <h1 className="font-heading text-4xl md:text-6xl font-semibold mt-4 mb-6 text-slate-800">
              Contact <span className="gradient-text">Us</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Have questions about GSLC? Want to partner with us? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Contact Content */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div variants={fadeInUp}>
              <div className="card-light rounded-2xl p-8 mb-8">
                <h3 className="font-heading text-2xl text-slate-800 mb-6">Office Address</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-100 to-red-100 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-slate-800 font-medium">GSLC Headquarters</p>
                      <p className="text-slate-500">2nd Floor, 214, Sankalp Tower</p>
                      <p className="text-slate-500">Jaipur, Rajasthan, India</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-700" />
                    </div>
                    <div>
                      <p className="text-slate-800 font-medium">Email</p>
                      <a href="mailto:info@gslc.co.in" className="text-red-600 hover:text-red-700 transition-colors">
                        info@gslc.co.in
                      </a>
                    </div>
                  </div>
                </div>

                <a
                  href="https://maps.app.goo.gl/bkGk3pDUUMbNYbh5A"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="google-maps-link"
                  className="mt-6 inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 transition-colors font-medium"
                >
                  <ExternalLink size={18} />
                  View on Google Maps
                </a>
              </div>

              {/* Google Maps Embed */}
              <div className="rounded-2xl overflow-hidden border border-slate-200 h-80 shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3557.7997364685474!2d75.78685231504694!3d26.91243448312626!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396db40a3c0e0c99%3A0x3f0000000000!2sSankalp%20Tower!5e0!3m2!1sen!2sin!4v1629894567890!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="GSLC Office Location"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div variants={fadeInUp}>
              <form onSubmit={handleSubmit} className="card-light rounded-2xl p-8 space-y-6" data-testid="contact-form">
                <h3 className="font-heading text-2xl text-slate-800 mb-2">Send us a Message</h3>
                <p className="text-slate-500 text-sm mb-6">Fill out the form below and we'll get back to you shortly.</p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-slate-600 mb-2 font-medium">Name *</label>
                    <input
                      type="text"
                      required
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-2 font-medium">Email *</label>
                    <input
                      type="email"
                      required
                      data-testid="contact-email-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-slate-600 mb-2 font-medium">Phone *</label>
                    <input
                      type="tel"
                      required
                      data-testid="contact-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-2 font-medium">Organization *</label>
                    <input
                      type="text"
                      required
                      data-testid="contact-organization-input"
                      value={formData.organization}
                      onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all"
                      placeholder="School/Institution name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-2 font-medium">Message *</label>
                  <textarea
                    required
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="w-full btn-gradient disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? "Sending..." : (
                    <>
                      Send Message <Send size={18} />
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default ContactPage;
